/**
 * client/hud/layout.ts — the BO2 LUI virtual screen and anchor math (pure, no DOM).
 *
 * OWNER: HUD-UI-AUDIO agent.
 *
 * BO2's Lua UI lays everything out on a 1280x720 virtual screen [hud.md "Coordinate system"].
 * An element is placed with setLeftRight(leftAnchor, rightAnchor, left, right) and
 * setTopBottom(topAnchor, bottomAnchor, top, bottom): offsets are measured from the anchored
 * edge(s), or from the centre when neither edge is anchored (ui/lui/luielement.lua).
 * Most HUD menus live in the user safe area (CoD.Menu.NewSafeAreaFromState); the console
 * title-safe constants are CoD.HDSafeWidth=1152 x CoD.HDSafeHeight=648, i.e. 90 % centred
 * (ui/t6/codbase.lua:499-502). hud-layout.json "safeArea.recommended" = [1152, 648].
 *
 * Non-16:9 windows: the virtual HEIGHT stays 720 and the virtual width grows/shrinks with the
 * aspect, so left/right anchored elements stick to the real screen edges (ESTIMATE: LUI's
 * behaviour on 16:10/ultrawide PC). When the window is narrower than 16:9 we keep the width at
 * 1280 and grow the height instead, so nothing is ever cropped.
 */

export const VIRTUAL_W = 1280;
export const VIRTUAL_H = 720;
/** Console title-safe fraction: 1152/1280 = 648/720 = 0.9 [hud.md, codbase.lua:499-502]. */
export const SAFE_FRAC = 0.9;
/** Code-drawn / hudelem parts (hitmarker, crosshair, damage arrows, wager end screen) use the
 *  old 640x480 HUD space; at 720p they are scaled by 720/480 = 1.5 [hud.md]. */
export const HUD640_SCALE = VIRTUAL_H / 480;

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Viewport {
  /** Real pixels per virtual unit (CSS px; multiply by devicePixelRatio for canvas pixels). */
  scale: number;
  /** Virtual screen size (1280x720 at 16:9). */
  vw: number;
  vh: number;
  /** Full virtual screen. */
  screen: Rect;
  /** User safe area (90 % centred by default). */
  safe: Rect;
}

export function computeViewport(cssW: number, cssH: number, safeFrac = SAFE_FRAC): Viewport {
  const w = Math.max(1, cssW), h = Math.max(1, cssH);
  const scale = w / h >= VIRTUAL_W / VIRTUAL_H ? h / VIRTUAL_H : w / VIRTUAL_W;
  const vw = w / scale, vh = h / scale;
  const sw = vw * safeFrac, sh = vh * safeFrac;
  return {
    scale, vw, vh,
    screen: { x: 0, y: 0, w: vw, h: vh },
    safe: { x: (vw - sw) / 2, y: (vh - sh) / 2, w: sw, h: sh },
  };
}

/** Horizontal anchor: 'left' = (true,false), 'right' = (false,true), 'center' = (false,false),
 *  'stretch' = (true,true) i.e. left offset from the left edge, right offset from the right edge. */
export type HAnchor = 'left' | 'right' | 'center' | 'stretch';
export type VAnchor = 'top' | 'bottom' | 'center' | 'stretch';

/** LUI setLeftRight / setTopBottom resolved against a parent rect. Returns x0 <= x1. */
export function anchorX(area: Rect, a: HAnchor, left: number, right: number): [number, number] {
  let x0: number, x1: number;
  switch (a) {
    case 'left': x0 = area.x + left; x1 = area.x + right; break;
    case 'right': x0 = area.x + area.w + left; x1 = area.x + area.w + right; break;
    case 'center': x0 = area.x + area.w / 2 + left; x1 = area.x + area.w / 2 + right; break;
    case 'stretch': x0 = area.x + left; x1 = area.x + area.w + right; break;
  }
  return x0 <= x1 ? [x0, x1] : [x1, x0];
}

export function anchorY(area: Rect, a: VAnchor, top: number, bottom: number): [number, number] {
  let y0: number, y1: number;
  switch (a) {
    case 'top': y0 = area.y + top; y1 = area.y + bottom; break;
    case 'bottom': y0 = area.y + area.h + top; y1 = area.y + area.h + bottom; break;
    case 'center': y0 = area.y + area.h / 2 + top; y1 = area.y + area.h / 2 + bottom; break;
    case 'stretch': y0 = area.y + top; y1 = area.y + area.h + bottom; break;
  }
  return y0 <= y1 ? [y0, y1] : [y1, y0];
}

export function anchorRect(area: Rect, ha: HAnchor, left: number, right: number, va: VAnchor, top: number, bottom: number): Rect {
  const [x0, x1] = anchorX(area, ha, left, right);
  const [y0, y1] = anchorY(area, va, top, bottom);
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}
